import { CommonModule } from '@angular/common';
import { Component, Input, computed, signal } from '@angular/core';
import { RouterModule } from '@angular/router';

import { fullName, initials } from '../../../helpers/user.helpers';
import { UserRef } from '../../../types/user.types';

/**
 * Cellule de présentation d'un utilisateur — usage transversal (tables,
 * en-têtes de fiche, listings divers). Remplace les ids bruts (`#42`,
 * `Utilisateur #12`) par avatar + nom + email/username, avec lien
 * cliquable optionnel vers la page user admin.
 *
 * Trois variantes via `size` :
 *   - `xs` : avatar 24px, une seule ligne nom — pour les tables denses
 *   - `sm` (défaut) : avatar 36px, nom + sous-titre — pour les listes
 *   - `md` : avatar 56px, nom + email + téléphone — pour les fiches détail
 *
 * Si `user` est null/undefined et `fallbackId` est fourni, affiche
 * `Utilisateur #{fallbackId}` en gris. Sinon « — ».
 */
@Component({
  selector: 'app-user-cell',
  imports: [CommonModule, RouterModule],
  template: `
    @if (display(); as d) {
      <div class="flex items-center gap-3" [class.gap-2]="size === 'xs'">
        <!-- Avatar -->
        @if (d.avatarUrl) {
          <img
            [src]="d.avatarUrl"
            [alt]="d.primary"
            class="rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-700"
            [class.h-6]="size === 'xs'"
            [class.w-6]="size === 'xs'"
            [class.h-9]="size === 'sm'"
            [class.w-9]="size === 'sm'"
            [class.h-14]="size === 'md'"
            [class.w-14]="size === 'md'"
            (error)="onImgError()" />
        } @else {
          <span
            class="flex shrink-0 items-center justify-center rounded-full font-medium"
            [class.h-6]="size === 'xs'"
            [class.w-6]="size === 'xs'"
            [class.text-[10px]]="size === 'xs'"
            [class.h-9]="size === 'sm'"
            [class.w-9]="size === 'sm'"
            [class.text-xs]="size === 'sm'"
            [class.h-14]="size === 'md'"
            [class.w-14]="size === 'md'"
            [class.text-base]="size === 'md'"
            [ngClass]="d.colorClass">
            {{ d.initials }}
          </span>
        }

        <!-- Text -->
        <div class="min-w-0 flex-1">
          @if (resolvedLink; as rl) {
            <a
              [routerLink]="rl"
              class="block truncate font-medium text-gray-800 hover:text-brand-700 dark:text-white/90 dark:hover:text-brand-400"
              [class.text-xs]="size === 'xs'"
              [class.text-sm]="size !== 'xs'">
              {{ d.primary }}
            </a>
          } @else {
            <span
              class="block truncate font-medium text-gray-800 dark:text-white/90"
              [class.text-xs]="size === 'xs'"
              [class.text-sm]="size !== 'xs'">
              {{ d.primary }}
            </span>
          }
          @if (size !== 'xs' && d.secondary) {
            <span class="block truncate text-xs text-gray-500 dark:text-gray-400">
              {{ d.secondary }}
            </span>
          }
          @if (size === 'md' && d.tertiary) {
            <span class="block truncate text-xs text-gray-500 dark:text-gray-400">
              {{ d.tertiary }}
            </span>
          }
        </div>
      </div>
    } @else {
      <span class="text-sm text-gray-400 dark:text-gray-500">—</span>
    }
  `,
})
export class UserCellComponent {
  @Input() user: UserRef | null | undefined = null;

  /** Affiché en fallback (« Utilisateur #{n} ») si `user` est absent. */
  @Input() fallbackId: number | null | undefined = null;

  /** `xs` table dense | `sm` liste (défaut) | `md` fiche détail */
  @Input() size: 'xs' | 'sm' | 'md' = 'sm';

  /**
   * Si fourni, le nom devient un lien cliquable. Typiquement
   * `['/users', user.authUserId]`. Si non fourni mais que `user.authUserId`
   * existe, on lie automatiquement vers `/users/{authUserId}` SAUF si
   * `noAutoLink` est true.
   */
  @Input() link: unknown[] | null = null;

  /** Désactive l'auto-lien vers /users/:id quand `link` n'est pas fourni. */
  @Input() noAutoLink = false;

  private readonly imgFailed = signal(false);

  readonly display = computed(() => {
    const u = this.user;
    if (!u) {
      const fid = this.fallbackId;
      if (fid != null) {
        return {
          primary: `Utilisateur #${fid}`,
          secondary: '',
          tertiary: '',
          initials: '?',
          colorClass: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300',
          avatarUrl: null as string | null,
        };
      }
      return null;
    }
    const primary = fullName(u) || u.username || u.email || `#${u.authUserId ?? '?'}`;
    const secondary = u.email && primary !== u.email ? u.email : u.username && primary !== u.username ? u.username : '';
    const tertiary = u.phone ?? '';
    return {
      primary,
      secondary,
      tertiary,
      initials: initials(u),
      colorClass: this.colorFor(primary),
      avatarUrl: this.imgFailed() ? null : u.avatarUrl,
    };
  });

  onImgError(): void {
    this.imgFailed.set(true);
  }

  /** Auto-link to user detail when no explicit link is provided. */
  get resolvedLink(): unknown[] | null {
    if (this.link) return this.link;
    if (this.noAutoLink) return null;
    const id = this.user?.authUserId;
    return id != null ? ['/users', id] : null;
  }

  private colorFor(name: string): string {
    const colors = [
      'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400',
      'bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300',
      'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
      'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
      'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
      'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300',
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300',
      'bg-error-100 text-error-700 dark:bg-error-500/15 dark:text-error-300',
    ];
    const sum = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return colors[sum % colors.length];
  }
}
